import winston, { transport } from 'winston'
import { CustomNextApiRequest } from '../../_utils/routeHandler'
import { NextApiResponse } from 'next'
import TransportStream from 'winston-transport'
import createHttpError from 'http-errors'
import { getUserIdForLogging } from '../securityService'

interface NewRelicTransportOptions
	extends TransportStream.TransportStreamOptions {
	apiKey: string
}

interface RequestContext {
	path?: string
	referer?: string | string[]
	userAgent?: string
	method?: string
	host?: string
	ip?: { [key: string]: string | string[] }
	statusCode?: number
	vercelCache?: string | string[]
	userId?: number
	correlationId: string | string[]
	query?: { [key: string]: string | string[] }
}

const API_KEY = process.env.NEW_RELIC_LOGGING_API_KEY
const NEW_RELIC_URL = 'https://log-api.newrelic.com/log/v1'
const ENVIRONMENT = process.env.NEXT_PUBLIC_ENVIRONMENT
const IS_LOCAL = ENVIRONMENT === 'LOCAL'
const IS_ACCEPTANCE = ENVIRONMENT === 'ACCEPTANCE'

class NewRelicTransport extends TransportStream {
	private apiKey: string

	constructor(opts: NewRelicTransportOptions) {
		super(opts)
		this.apiKey = opts.apiKey
	}

	log(info: winston.Logform.TransformableInfo, callback: () => void): void {
		setImmediate(() => {
			this.emit('logged', info)
		})

		const messageSymbol = Object.getOwnPropertySymbols(info).find(
			(symbol) => symbol.toString() === 'Symbol(message)'
		)
		const message = messageSymbol ? info[messageSymbol] : info.message

		const payload = [
			{
				common: {
					attributes: {
						logtype: 'NextJs-App-via-logAPI',
						projectName: IS_LOCAL
							? 'musicapp-local'
							: `anw-musicapp-${ENVIRONMENT?.toLowerCase()}`,
						environment: ENVIRONMENT,
					},
				},
				logs: [
					{
						timestamp: info.timestamp,
						message: message,
						// Spread additional meta attributes if they exist
						...(info.meta && { attributes: info.meta }),
					},
				],
			},
		]

		fetch(NEW_RELIC_URL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Api-Key': this.apiKey,
				Accept: '*/*',
			},
			body: JSON.stringify(payload),
		})
			.then((response) => {
				if (!response.ok) {
					throw createHttpError(
						response.status,
						`HTTP error status: ${response.status}`
					)
				}
				return response.json()
			})
			.catch((error: Error | unknown) => {
				console.error('Error sending log:', error)
				console.log(`Failed payload:`, JSON.stringify(payload))
			})

		callback()
	}
}

const {
	combine,
	timestamp,
	label,
	prettyPrint,
	json,
	errors,
	colorize,
	padLevels,
	align,
	printf,
} = winston.format

const extractRequestContext = (
	req: CustomNextApiRequest,
	res: NextApiResponse
): RequestContext => ({
	path: req.url,
	referer: req.headers['referer'],
	userAgent: req.headers['user-agent'],
	method: req.method,
	host: req.headers.host,
	ip: {
		cloudfrontViewerAddress: req.headers['cloudfront-viewer-address'] ?? null,
		xForwardedFor: req.headers['x-forwarded-for'] ?? null,
		remoteAddress: req.socket?.remoteAddress,
	},
	statusCode: res?.statusCode,
	vercelCache: req.headers['x-vercel-cache'] ?? 'MISS',
	userId: req?.session?.userId ?? getUserIdForLogging(req),
	correlationId: req.headers['x-correlation-id'],
	query: req.query,
})

const devMessageFormat = (info: winston.Logform.TransformableInfo): string => {
	const formatMessage = (): string =>
		`[${info.timestamp}] [${info.label}]@[${info.level}]:${info.message}`
	const formatError = (): string =>
		`[${info.timestamp}] [${info.label}]@[${info.level}]:${info.message}\n\n${info.err.stack}\n`
	const format: string = Object.prototype.hasOwnProperty.call(info, 'err')
		? formatError()
		: formatMessage()
	return format
}

const customFormat = winston.format(
	(info: winston.Logform.TransformableInfo) => {
		if (info.req && info.res) {
			const requestContext = extractRequestContext(info.req, info.res)
			info = { ...info, ...requestContext }
			delete info.req
			delete info.res
		}
		return info
	}
)

const consoleFormat =
	process.env.NODE_ENV === 'development'
		? {
				format: combine(
					colorize({ all: true }),
					padLevels(),
					timestamp({
						format: 'HH-MM:ss YYYY-MM-DD',
					}),
					prettyPrint(),
					align(),
					printf((info) => devMessageFormat(info))
				),
		  }
		: {}

const transports: transport[] = [new winston.transports.Console(consoleFormat)]

if (!IS_LOCAL && !IS_ACCEPTANCE) {
	transports.push(new NewRelicTransport({ apiKey: API_KEY }))
}

export const logger = winston.createLogger({
	format: combine(
		customFormat(),
		errors({ stack: true }),
		label({ label: 'Api Logging for the MusicApp' }),
		timestamp(),
		prettyPrint(),
		json()
	),
	transports,
})
